import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const LoginScreen: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const { signInWithPassword, signUp, signInWithEmail } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Email validation
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Forgot password flow (magic link)
    if (showForgotPassword) {
      const result = await signInWithEmail(email);
      if (result.error) {
        setError(result.error.message || 'Failed to send magic link');
        setLoading(false);
      } else {
        setEmailSent(true);
        setLoading(false);
      }
      return;
    }

    // Password validation
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    // Sign up or sign in
    const result = isSignUp
      ? await signUp(email, password)
      : await signInWithPassword(email, password);

    if (result.error) {
      setError(result.error.message || 'Authentication failed');
      setLoading(false);
    } else {
      setLoading(false);
      // If signup, show confirmation message
      // If signin, user will be automatically logged in
      if (isSignUp) {
        setSignupSuccess(true);
      }
    }
  };

  // Signup success - email confirmation required
  if (signupSuccess) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'grey.100',
          p: 2,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <Box
            component="img"
            src="/apple-touch-icon.png"
            alt="US Mastering Online"
            sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }}
          />
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Account Created! ✉️
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            We've sent a confirmation email to:
          </Typography>
          <Typography variant="h6" color="primary" sx={{ mb: 3 }}>
            {email}
          </Typography>
          <Alert severity="success" sx={{ textAlign: 'left', mb: 2 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Click the confirmation link in your email to activate your account
            </Typography>
            <Typography variant="body2">
              • Check your inbox and spam folder
              <br />
              • The link expires in 24 hours
              <br />
              • After confirming, you can sign in and start with 3 free credits!
            </Typography>
          </Alert>
          <Button
            variant="text"
            onClick={() => {
              setSignupSuccess(false);
              setIsSignUp(false);
              setEmail('');
              setPassword('');
            }}
            sx={{ mt: 2 }}
          >
            Back to sign in
          </Button>
        </Paper>
      </Box>
    );
  }

  // Magic link sent confirmation screen
  if (emailSent) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'grey.100',
          p: 2,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <Box
            component="img"
            src="/apple-touch-icon.png"
            alt="US Mastering Online"
            sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }}
          />
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Check Your Email! ✉️
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            We've sent a magic link to:
          </Typography>
          <Typography variant="h6" color="primary" sx={{ mb: 3 }}>
            {email}
          </Typography>
          <Alert severity="info" sx={{ textAlign: 'left', mb: 2 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Click the link in your email to sign in
            </Typography>
            <Typography variant="body2">
              • Check your inbox and spam folder
              <br />
              • The link expires in 1 hour
              <br />• You can close this window
            </Typography>
          </Alert>
          <Button
            variant="text"
            onClick={() => {
              setEmailSent(false);
              setShowForgotPassword(false);
              setEmail('');
            }}
            sx={{ mt: 2 }}
          >
            Back to sign in
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'grey.100',
        p: 2,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 450, width: '100%' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            component="img"
            src="/apple-touch-icon.png"
            alt="US Mastering Online"
            sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }}
          />
          <Typography variant="h5" gutterBottom fontWeight={600}>
            US Mastering Online
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Professional audio mastering by Engineer Ludwing Diaz
          </Typography>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
          {showForgotPassword ? 'Reset Password' : isSignUp ? 'Create Account' : 'Sign In'}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {showForgotPassword
            ? "Enter your email and we'll send you a magic link to sign in."
            : isSignUp
            ? 'Create your account to get started with 3 free credits.'
            : 'Sign in to your account to continue.'}
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            placeholder="you@example.com"
            sx={{ mb: 2 }}
            autoFocus
          />

          {!showForgotPassword && (
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              helperText={isSignUp ? 'At least 6 characters' : ''}
              placeholder="••••••••"
              sx={{ mb: 1 }}
            />
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Forgot password link - only show on sign in */}
          {!isSignUp && !showForgotPassword && (
            <Box sx={{ textAlign: 'right', mb: 2 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => {
                  setShowForgotPassword(true);
                  setError('');
                  setPassword('');
                }}
                disabled={loading}
                sx={{ cursor: 'pointer' }}
              >
                Forgot password?
              </Link>
            </Box>
          )}

          {/* Back link for forgot password */}
          {showForgotPassword && (
            <Box sx={{ textAlign: 'right', mb: 2 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => {
                  setShowForgotPassword(false);
                  setError('');
                }}
                disabled={loading}
                sx={{ cursor: 'pointer' }}
              >
                Back to sign in
              </Link>
            </Box>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading || !email || (!showForgotPassword && !password)}
            sx={{ mb: 2 }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {showForgotPassword ? 'Sending...' : isSignUp ? 'Creating account...' : 'Signing in...'}
              </>
            ) : showForgotPassword ? (
              'Send Magic Link'
            ) : isSignUp ? (
              'Create Account'
            ) : (
              'Sign In'
            )}
          </Button>
        </form>

        {/* Sign up / Sign in toggle */}
        {!showForgotPassword && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setError('');
                  setPassword('');
                }}
                disabled={loading}
                sx={{ fontWeight: 600, cursor: 'pointer' }}
              >
                {isSignUp ? 'Sign In' : 'Sign Up'}
              </Link>
            </Typography>
          </Box>
        )}

        {isSignUp && (
          <Alert severity="info" sx={{ mt: 3, fontSize: '0.85rem' }}>
            <Typography variant="body2">
              <strong>New users get 3 free credits!</strong> Each credit processes one full song.
            </Typography>
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default LoginScreen;

